import React, { useEffect, useState } from 'react';
import api, { fetchAllPages } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarShiftDrawer } from '@/components/calendar/CalendarShiftDrawer';
import { useAuth } from '@/lib/auth';

/**
 * ParticipantRostersTab — participant-filtered view of shifts (/api/roster/shifts?participantId=…).
 * #6: a row opens the same shift drawer as the Scheduling Board, so an admin can assign / manage the
 * shift from here; house and branch show by name, with the agreement and line item.
 */
export function ParticipantRostersTab({ participantId, refreshKey = 0 }) {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [reload, setReload] = useState(0);
  const [branches, setBranches] = useState({});
  const [staff, setStaff] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setDenied(false);
    api.get(`/roster/shifts?participantId=${participantId}&pageSize=50`)
      .then((r) => {
        if (cancelled) return;
        const items = r?.data?.items || r?.data?.data || r?.data || [];
        // #20: hide shifts replaced by an agreement-schedule edit (they are superseded, not real cancellations)
        setShifts(Array.isArray(items) ? items.filter(s => s.cancellation_type !== 'SCHEDULE_CHANGE') : []);
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) setDenied(true);
        else setError('Unable to load rosters for this participant.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId, refreshKey, reload]);

  useEffect(() => {
    api.get('/org/branches').then((r) => {
      const map = {};
      (r.data?.items || r.data?.data || []).forEach((b) => { [b.id, b._id, b.code].filter(Boolean).forEach((k) => { map[k] = b.name; }); });
      setBranches(map);
    }).catch(() => {});
  }, []);

  const openShift = async (s) => {
    if (staff === null) setStaff(await fetchAllPages('/staff').catch(() => []));   // drawer needs the staff list to assign
    setSelected({ ...s, branch_name: s.branch_name || branches[s.branch_id] });
  };

  if (denied) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="participant-rosters-denied">
        You do not have permission to view roster information for this participant.
      </div>
    );
  }
  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="participant-rosters-loading">Loading rosters…</div>;
  }
  if (error) {
    return <div className="p-6 text-sm text-red-600" data-testid="participant-rosters-error">{error}</div>;
  }
  if (shifts.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground text-center" data-testid="participant-rosters-empty">
        No shifts linked to this participant.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="participant-rosters-tab">
      <h3 className="text-sm font-semibold" data-testid="participant-rosters-count">
        Linked Shifts ({shifts.length})
      </h3>
      <div className="space-y-2">
        {shifts.map((s) => {
          const id = s.id || s._id;
          const branchName = s.branch_id ? (branches[s.branch_id] || s.branch_id) : null;
          return (
            <Card key={id} data-testid={`participant-roster-row-${id}`} role="button" tabIndex={0}
              className="cursor-pointer hover:border-primary/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              onClick={() => openShift(s)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openShift(s); } }}>
              <CardContent className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {s.service_name || s.title || s.shift_type || 'Shift'}
                    <span className="text-xs text-muted-foreground ml-2">{s.date}</span>
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {s.start_time || '—'} – {s.end_time || '—'}
                    {(s.house_name || s.house_id) ? <span className="ml-2">House: {s.house_name || s.house_id}</span> : null}
                    {branchName ? <span className="ml-2">Branch: {branchName}</span> : null}
                    {s.agreement_no ? <span className="ml-2">Agreement: {s.agreement_no}</span> : null}
                    {s.billing_source === 'FUNDING_PLAN' ? <span className="ml-2">No agreement · funding plan</span> : null}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {(s.assigned_staff || []).length ? `Worker: ${(s.assigned_staff || []).map((a) => a.name).filter(Boolean).join(', ')}` : 'Unassigned — click to assign a worker'}
                  </p>
                </div>
                <Badge variant="outline" className="text-[10px]">{s.status || '—'}</Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <CalendarShiftDrawer shift={selected} open={!!selected} onClose={() => setSelected(null)} staff={staff || []}
        permissions={user?.permissions || []} onUpdate={() => setReload((n) => n + 1)} />
    </div>
  );
}

export default ParticipantRostersTab;
